import { usePostHog } from '@posthog/react';
import { isAxiosError, type AxiosError } from 'axios';
import { useCallback } from 'react';
import { useSnackbar } from '~/providers/SnackbarProvider';
import { logger } from '~/scripts/logger';
import useDataLayer from '~/services/datalayer';

export interface HandleExceptionDetails {
  /**
   * category to send to analytics
   */
  category?: string;
  /**
   * message is to overwrite e.message
   */
  message?: string;
}

export const useHandleException = () => {
  const { openSnackbar } = useSnackbar();
  const dataLayer = useDataLayer();
  const posthog = usePostHog();
  return useCallback(
    (e: Error | AxiosError, details?: HandleExceptionDetails): void => {
      const message = getExceptionMessage(e, details);
      if (message === 'AbortError') {
        return;
      }

      openSnackbar(message, 'error');
      if (details?.category) {
        dataLayer.push({
          event: 'error',
          category: details.category,
          message,
          url: window.location.href,
        });
      }
      if (typeof window !== 'undefined') {
        posthog.captureException(e);
      }
    },
    [dataLayer, openSnackbar, posthog]
  );
};

function getExceptionMessage(
  e: Error | AxiosError,
  details?: HandleExceptionDetails
): string {
  if (isAxiosError(e)) {
    return e.response?.data?.message || e?.message;
  }
  if (e instanceof Error) {
    return e.message;
  }
  if (details?.message) {
    return details.message;
  }
  logger.warn(`unknown error type`, e);
  return ((e as unknown) || 'unknown error').toString();
}
